import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';

import { SystemSlipType } from '../../bet-generation/slip-type';
import { SystemKey } from '../models';
import SystemBetsService from './system-bets.service';

function range(start: number, end: number): number[] {
    const array: number[] = new Array(end - start);
    let i = 0;
    for (let item = start; item < end; item++) {
        array[i] = item;
        i++;
    }

    return array;
}

@Injectable()
export default class SystemFactoryService {
    private systems: Record<number, SystemSlipType[]> = {};

    constructor(
        private systemBetsService: SystemBetsService,
        private betslipConfig: BetslipConfig,
    ) {
        this.generateSystems();
    }

    getSystemsByPicksCount(picksCount: number): SystemSlipType[] {
        return !this.systems[picksCount] ? [] : [...this.systems[picksCount]];
    }

    private generateSystems(): void {
        range(this.betslipConfig.minSystemBets, this.betslipConfig.maxSystemBets + 1).forEach((count) => {
            this.systems[count] = this.generateSystemsByPicksCount(count);
        });
    }

    private generateSystemsByPicksCount(picksCount: number): SystemSlipType[] {
        const basicSystems = this.getBasicSystems(picksCount);
        const fullSystems = this.getFullSystems(picksCount);

        return basicSystems.concat(fullSystems);
    }

    private getBasicSystems(picksCount: number): SystemSlipType[] {
        let combinationsLenghts: number[];

        // We need to return only systems with less than 500 combinations
        if (picksCount < 3) {
            combinationsLenghts = [];
        } else if (picksCount <= 11) {
            combinationsLenghts = range(2, picksCount);
        } else if (picksCount === 12) {
            combinationsLenghts = range(2, 5).concat(range(8, 12));
        } else if (picksCount <= 15) {
            combinationsLenghts = range(2, 4).concat(range(picksCount - 3, picksCount));
        } else if (picksCount <= 32) {
            combinationsLenghts = [2, picksCount - 2, picksCount - 1];
        } else {
            combinationsLenghts = [picksCount - 1];
        }

        return combinationsLenghts.map(
            (combinationsLength) =>
                new SystemSlipType(
                    this.betslipConfig.isSystemMOfNEnabled ? SystemKey.MOfN : this.getSystemBasicKey(combinationsLength, picksCount),
                    this.systemBetsService.getSystemBasicName(combinationsLength, picksCount),
                    [combinationsLength],
                ),
        );
    }

    private getFullSystems(picksCount: number): SystemSlipType[] {
        const result = [];

        if (picksCount >= 3 && picksCount <= 8) {
            const systemWithoutSinglesKey = this.getSystemFullWithoutSinglesKey(picksCount);
            const systemWithoutSinglesName = this.systemBetsService.getSystemFullName(systemWithoutSinglesKey);
            const systemWithoutSinglesBasis = this.getFullSystemBasis(picksCount, false);
            result.push(new SystemSlipType(systemWithoutSinglesKey, systemWithoutSinglesName, systemWithoutSinglesBasis));

            if (picksCount <= 6) {
                const systemWithSinglesKey = this.getSystemFullWithSinglesKey(picksCount);
                const systemWithSinglesName = this.systemBetsService.getSystemFullName(systemWithSinglesKey);
                const systemWithSinglesBasis = this.getFullSystemBasis(picksCount, true);
                result.push(new SystemSlipType(systemWithSinglesKey, systemWithSinglesName, systemWithSinglesBasis));
            }
        }

        return result;
    }

    private getSystemBasicKey(combinationsLenght: number, picksCount: number): string {
        return `System${combinationsLenght}Of${picksCount}`;
    }

    private getSystemFullWithSinglesKey(picksCount: number): string {
        switch (picksCount) {
            case 3:
                return SystemKey.Patent;
            case 4:
                return SystemKey.Lucky15;
            case 5:
                return SystemKey.Lucky31;
            case 6:
                return SystemKey.Lucky63;
        }
        throw Error(`No full system with singles for ${picksCount} pick.`);
    }

    private getSystemFullWithoutSinglesKey(picksCount: number): string {
        switch (picksCount) {
            case 3:
                return SystemKey.Trixie;
            case 4:
                return SystemKey.Yankee;
            case 5:
                return SystemKey.Canadian;
            case 6:
                return SystemKey.Heinz;
            case 7:
                return SystemKey.SuperHeinz;
            case 8:
                return SystemKey.Goliath;
        }

        throw Error(`No full system without singles for ${picksCount} pick.`);
    }

    private getFullSystemBasis(picksCount: number, includeSingles: boolean): number[] {
        return range(includeSingles ? 1 : 2, picksCount + 1);
    }
}
